import {
  createContext,
  useCallback,
  use,
  useEffect,
  useLayoutEffect,
  useMemo,
  type ReactNode,
} from "react";
import { toast } from "~/lib/toast";
import { normalizeVmId, type VmId } from "~/lib/auth/vm-types";
import { startUserAction } from "~/lib/user-action-observer";
import type { UserActionName } from "~/lib/user-actions";
import {
  clearSiweAuthProfile,
  activateSyntheticDevAuthProfile,
  clearSyntheticDevAuthProfile,
  GUEST_TRANSPORT_SNAPSHOT,
  getAppAuthTransportAuthHeaders,
  getAppAuthTransportSnapshot,
  getSiweAuthProfile,
  getSyntheticWalletAddresses,
  setAppAuthTransportSnapshot,
  updateSiweAuthProfile,
  updateSyntheticDevAuthProfile,
  useSiweAuthProfile,
  useSiweEvmSignerProfile,
  useSyntheticDevAuthProfile,
  type AppAuthTransportSnapshot,
  type SiweAuthProfile,
  type SiweAuthProfilePatch,
  type SiweEvmSignerProfile,
  type SyntheticDevAuthProfile,
  type SyntheticDevAuthProfilePatch,
  type SyntheticDevAuthTarget,
} from "./app-auth-runtime";
import {
  resolveSyntheticDevEvmSigner,
  resolveWalletlessSiweEvmSigner,
} from "./synthetic-dev-evm-signer";
import type {
  AppAuthLinkedWallet,
  AppAuthUser,
  AppAuthWallet,
} from "./app-auth-types";
import { getPrivyAppId } from "~/lib/env";
import {
  refreshSiweAuthSession,
  revokeSiweAuthSession,
  SiweAuthRequestError,
} from "./siwe-auth-client";

const APP_AUTH_MISSING_PROVIDER_MESSAGE =
  "App auth provider is missing. Wrap the tree in a guest, SIWE, synthetic, or Privy auth provider.";
const SIWE_ACCESS_TOKEN_REFRESH_SKEW_MS = 30_000;
const SIWE_SESSION_REFRESH_RETRY_BASE_DELAY_MS = 1_000;
const SIWE_SESSION_REFRESH_RETRY_MAX_ATTEMPTS = 5;
const SIWE_SESSION_REFRESH_RETRY_MAX_DELAY_MS = 15_000;

export type AppAuthSource = "guest" | "privy" | "siwe" | "synthetic";
export type {
  AppAuthTransportSnapshot,
  SiweAuthProfile,
  SiweEvmSignerProfile,
  SyntheticDevAuthProfile,
  SyntheticDevAuthTarget,
};
export type { AppAuthUser } from "./app-auth-types";

export interface AppAuthCapabilities {
  canConnectExternalWallet: boolean;
  canExportWallet: boolean;
  canManageLinkedEmail: boolean;
  canSign: boolean;
}

export interface AppAuthValue {
  activateSyntheticProfile: (
    nextProfile: Omit<
      SyntheticDevAuthProfile,
      "devAuthToken" | "evmPrivateKey"
    > & {
      devAuthToken?: string | null;
      evmPrivateKey?: string | null;
    },
  ) => void;
  authenticated: boolean;
  capabilities: AppAuthCapabilities;
  clearSyntheticProfile: () => void;
  /**
   * Opens the runtime's connect-external-wallet flow (the Privy modal). Only
   * meaningful when `capabilities.canConnectExternalWallet` is true; other
   * auth sources warn and no-op.
   */
  connectWallet: () => void;
  exportWallet: () => Promise<void>;
  linkEmail: () => void;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  ready: boolean;
  siweEvmSignerProfile?: SiweEvmSignerProfile | null;
  siweProfile?: SiweAuthProfile | null;
  source: AppAuthSource;
  syntheticProfile: SyntheticDevAuthProfile | null;
  transport: AppAuthTransportSnapshot;
  updateEmail: () => void;
  updateSiweProfile?: (patch: SiweAuthProfilePatch) => void;
  updateSyntheticProfile: (patch: SyntheticDevAuthProfilePatch) => void;
  user: AppAuthUser | null;
}

const AppAuthContext = createContext<AppAuthValue | null>(null);

const EMPTY_CAPABILITIES: AppAuthCapabilities = {
  canConnectExternalWallet: false,
  canExportWallet: false,
  canManageLinkedEmail: false,
  canSign: false,
};

export function canAppAuthSign(
  input: Pick<AppAuthValue, "authenticated" | "capabilities">,
): boolean {
  return input.authenticated && input.capabilities.canSign;
}

export function getSigningDisabledMessage(actionLabel: string): string {
  return `${actionLabel} requires a signing-capable wallet session. For synthetic dev auth or local SIWE, provide a matching local EVM signer private key on /debug.`;
}

function normalizeWalletAddress(address: string): string {
  return address.startsWith("0x") ? address.toLowerCase() : address;
}

function buildWalletAccount(
  address: string,
  source: "glider-dev-auth" | "glider-siwe" = "glider-dev-auth",
): AppAuthWallet {
  const vmId = normalizeVmId(undefined, address);
  const chainTypeByVm: Readonly<Partial<Record<VmId, string>>> = {
    evm: "ethereum",
    solana: "solana",
    sui: "sui",
  };
  const chainType = chainTypeByVm[vmId] ?? vmId.replace(/^other:/u, "");

  return {
    address,
    chainType,
    connectorType: source,
    walletClientType: source,
  };
}

function buildLinkedWalletAccount(
  address: string,
  source: "glider-dev-auth" | "glider-siwe" = "glider-dev-auth",
): AppAuthLinkedWallet {
  return {
    ...buildWalletAccount(address, source),
    type: "wallet",
  };
}

function buildSyntheticUser(
  profile: SyntheticDevAuthProfile,
): AppAuthUser | null {
  const primaryWallet = buildWalletAccount(profile.walletAddress);
  const linkedAccounts = [buildLinkedWalletAccount(profile.walletAddress)];

  if (
    profile.secondaryWalletAddress &&
    normalizeWalletAddress(profile.secondaryWalletAddress) !==
      normalizeWalletAddress(profile.walletAddress)
  ) {
    linkedAccounts.push(
      buildLinkedWalletAccount(profile.secondaryWalletAddress),
    );
  }

  return {
    id:
      profile.userId ??
      `dev-auth:${normalizeWalletAddress(profile.walletAddress)}`,
    linkedAccounts,
    wallet: primaryWallet,
  };
}

function buildSiweUser(profile: SiweAuthProfile): AppAuthUser {
  const walletAddress = normalizeWalletAddress(profile.walletAddress);
  return {
    id: `siwe:${walletAddress}`,
    linkedAccounts: [buildLinkedWalletAccount(walletAddress, "glider-siwe")],
    wallet: buildWalletAccount(walletAddress, "glider-siwe"),
  };
}

function warnUnsupportedSyntheticAction(message: string): void {
  toast.warning(message, {
    description:
      "Synthetic dev auth only supports configured local signing flows. Use real Privy auth for wallet export or linked-email management.",
  });
}

function warnUnsupportedSiweAction(message: string): void {
  toast.warning(message, {
    description:
      "Local SIWE auth only supports bearer-token API calls and configured Walletless signing. Use real Privy auth for wallet export or linked-email management.",
  });
}

function warnGuestLoginUnavailable(): void {
  if (getPrivyAppId() === null) {
    toast.warning(
      "Sign-in is unavailable because VITE_PRIVY_APP_ID is not configured.",
      {
        description:
          "Set VITE_PRIVY_APP_ID in apps/webapp-v2/.env and restart Vite for real Privy auth, or open /debug to configure synthetic auth.",
      },
    );
    return;
  }

  toast.warning("Sign-in is unavailable in this guest runtime.", {
    description:
      "Open /debug to configure synthetic auth when you need a local authenticated session.",
  });
}

type SyntheticController = Pick<
  AppAuthValue,
  | "activateSyntheticProfile"
  | "clearSyntheticProfile"
  | "updateSyntheticProfile"
> & {
  profile: SyntheticDevAuthProfile | null;
};

type SiweController = {
  clearSiweProfile: () => void;
  profile: SiweAuthProfile | null;
  signerProfile: SiweEvmSignerProfile | null;
  updateSiweProfile: (patch: SiweAuthProfilePatch) => void;
};

export function useSyntheticProfileController(): SyntheticController {
  const profile = useSyntheticDevAuthProfile();

  const activateSyntheticProfile = useCallback<
    AppAuthValue["activateSyntheticProfile"]
  >((nextProfile) => {
    const normalizedProfile = activateSyntheticDevAuthProfile(nextProfile);
    if (!normalizedProfile) {
      toast.error("Invalid synthetic dev auth profile.");
    }
  }, []);

  const clearSyntheticProfile = useCallback(() => {
    clearSyntheticDevAuthProfile();
  }, []);

  const updateSyntheticProfile = useCallback<
    AppAuthValue["updateSyntheticProfile"]
  >((patch) => {
    updateSyntheticDevAuthProfile(patch);
  }, []);

  return {
    activateSyntheticProfile,
    clearSyntheticProfile,
    profile,
    updateSyntheticProfile,
  };
}

export function useSiweProfileController(): SiweController {
  const profile = useSiweAuthProfile();
  const signerProfile = useSiweEvmSignerProfile();

  const clearSiweProfile = useCallback(() => {
    clearSiweAuthProfile();
  }, []);

  const updateSiweProfile = useCallback((patch: SiweAuthProfilePatch) => {
    updateSiweAuthProfile(patch);
  }, []);

  return {
    clearSiweProfile,
    profile,
    signerProfile,
    updateSiweProfile,
  };
}

export function createGuestAuthValue(
  controller: Pick<
    SyntheticController,
    | "activateSyntheticProfile"
    | "clearSyntheticProfile"
    | "updateSyntheticProfile"
  >,
): AppAuthValue {
  return {
    activateSyntheticProfile: controller.activateSyntheticProfile,
    authenticated: false,
    capabilities: EMPTY_CAPABILITIES,
    clearSyntheticProfile: controller.clearSyntheticProfile,
    connectWallet: () => {
      warnUnsupportedSyntheticAction(
        "Wallet connection requires a real Privy-authenticated session.",
      );
    },
    exportWallet: async () => {
      warnUnsupportedSyntheticAction(
        "Wallet export requires a real Privy-authenticated wallet session.",
      );
    },
    linkEmail: () => {
      warnUnsupportedSyntheticAction(
        "Email linking requires a real Privy-authenticated wallet session.",
      );
    },
    login: async () => {
      warnGuestLoginUnavailable();
    },
    logout: async () => undefined,
    ready: true,
    siweEvmSignerProfile: null,
    siweProfile: null,
    source: "guest",
    syntheticProfile: null,
    transport: GUEST_TRANSPORT_SNAPSHOT,
    updateEmail: () => {
      warnUnsupportedSyntheticAction(
        "Email management requires a real Privy-authenticated wallet session.",
      );
    },
    updateSyntheticProfile: controller.updateSyntheticProfile,
    user: null,
  };
}

export function createPrivyAuthValue(
  input: {
    authenticated: boolean;
    connectWallet: () => void;
    exportWallet: () => Promise<void>;
    linkEmail: () => void;
    login: () => Promise<void>;
    logout: () => Promise<void>;
    ready: boolean;
    updateEmail: () => void;
    user: AppAuthUser | null;
  },
  controller: Pick<
    SyntheticController,
    | "activateSyntheticProfile"
    | "clearSyntheticProfile"
    | "updateSyntheticProfile"
  >,
): AppAuthValue {
  const walletAddress = input.user?.wallet?.address
    ? normalizeWalletAddress(input.user.wallet.address)
    : null;
  const walletVm = walletAddress
    ? normalizeVmId(input.user?.wallet?.chainType, walletAddress)
    : null;

  return {
    activateSyntheticProfile: controller.activateSyntheticProfile,
    authenticated: input.authenticated,
    capabilities: {
      canConnectExternalWallet: true,
      canExportWallet: true,
      canManageLinkedEmail: true,
      canSign: true,
    },
    clearSyntheticProfile: controller.clearSyntheticProfile,
    connectWallet: input.connectWallet,
    exportWallet: input.exportWallet,
    linkEmail: input.linkEmail,
    login: input.login,
    logout: input.logout,
    ready: input.ready,
    siweEvmSignerProfile: null,
    siweProfile: null,
    source: "privy",
    syntheticProfile: null,
    transport: {
      mode: "privy",
      devAuthToken: null,
      evmAddress: walletVm === "evm" ? walletAddress : null,
      solanaAddress: walletVm === "solana" ? walletAddress : null,
      userId: input.user?.id ?? null,
      walletAddress,
    },
    updateEmail: input.updateEmail,
    updateSiweProfile: undefined,
    updateSyntheticProfile: controller.updateSyntheticProfile,
    user: input.user,
  };
}

function getSiweAccessTokenRefreshDelayMs(
  profile: SiweAuthProfile,
): number | null {
  if (!profile.refreshToken) {
    return null;
  }

  if (!profile.accessToken || !profile.expiresAt) {
    return 0;
  }

  const expiresAtMs = new Date(profile.expiresAt).getTime();
  if (!Number.isFinite(expiresAtMs)) {
    return 0;
  }

  return Math.max(
    0,
    expiresAtMs - Date.now() - SIWE_ACCESS_TOKEN_REFRESH_SKEW_MS,
  );
}

function createSiweAuthValue(
  input: SiweController,
  syntheticController: Pick<
    SyntheticController,
    | "activateSyntheticProfile"
    | "clearSyntheticProfile"
    | "updateSyntheticProfile"
  >,
): AppAuthValue {
  if (!input.profile) {
    return createGuestAuthValue(syntheticController);
  }

  const user = buildSiweUser(input.profile);
  const hasTransport = Boolean(input.profile.accessToken);
  const evmSigner = resolveWalletlessSiweEvmSigner(input.signerProfile);

  return {
    activateSyntheticProfile: syntheticController.activateSyntheticProfile,
    authenticated: hasTransport,
    capabilities: {
      canConnectExternalWallet: false,
      canExportWallet: false,
      canManageLinkedEmail: false,
      canSign: hasTransport && evmSigner.enabled,
    },
    clearSyntheticProfile: syntheticController.clearSyntheticProfile,
    connectWallet: () => {
      warnUnsupportedSiweAction(
        "Local SIWE auth manages its signer directly; there is no wallet to connect.",
      );
    },
    exportWallet: async () => {
      warnUnsupportedSiweAction(
        "Local SIWE auth does not expose wallet export.",
      );
    },
    linkEmail: () => {
      warnUnsupportedSiweAction(
        "Local SIWE auth does not support linked-email management.",
      );
    },
    login: async () => {
      warnUnsupportedSiweAction(
        "Local SIWE auth is already managing this session.",
      );
    },
    logout: async () => {
      const profile = getSiweAuthProfile();
      input.clearSiweProfile();
      if (profile?.accessToken) {
        await revokeSiweAuthSession({
          accessToken: profile.accessToken,
          sessionId: profile.sessionId,
        }).catch(() => undefined);
      }
      toast.info("Local SIWE auth disabled.");
    },
    ready: true,
    siweEvmSignerProfile: input.signerProfile,
    siweProfile: input.profile,
    source: "siwe",
    syntheticProfile: null,
    transport: {
      mode: "siwe",
      devAuthToken: null,
      evmAddress: normalizeWalletAddress(input.profile.walletAddress),
      siweAccessToken: input.profile.accessToken,
      solanaAddress: null,
      userId: user.id ?? null,
      walletAddress: normalizeWalletAddress(input.profile.walletAddress),
    },
    updateEmail: () => {
      warnUnsupportedSiweAction(
        "Local SIWE auth does not support linked-email management.",
      );
    },
    updateSiweProfile: input.updateSiweProfile,
    updateSyntheticProfile: syntheticController.updateSyntheticProfile,
    user,
  };
}

function createSyntheticAuthValue(input: SyntheticController): AppAuthValue {
  if (!input.profile) {
    return createGuestAuthValue(input);
  }

  const user = buildSyntheticUser(input.profile);
  const { evmAddress, solanaAddress } = getSyntheticWalletAddresses(
    input.profile,
  );
  const hasTransport = Boolean(input.profile.devAuthToken);
  const evmSigner = resolveSyntheticDevEvmSigner(input.profile);

  return {
    activateSyntheticProfile: input.activateSyntheticProfile,
    authenticated: hasTransport,
    capabilities: {
      canConnectExternalWallet: false,
      canExportWallet: false,
      canManageLinkedEmail: false,
      canSign: hasTransport && evmSigner.enabled,
    },
    clearSyntheticProfile: input.clearSyntheticProfile,
    connectWallet: () => {
      warnUnsupportedSyntheticAction(
        "Synthetic dev auth manages its signer directly; there is no wallet to connect.",
      );
    },
    exportWallet: async () => {
      warnUnsupportedSyntheticAction(
        "Synthetic dev auth does not expose wallet export.",
      );
    },
    linkEmail: () => {
      warnUnsupportedSyntheticAction(
        "Synthetic dev auth does not support linked-email management.",
      );
    },
    login: async () => {
      warnUnsupportedSyntheticAction(
        "Synthetic dev auth is already managing this session.",
      );
    },
    logout: async () => {
      input.clearSyntheticProfile();
      toast.info("Synthetic dev auth disabled.");
    },
    ready: true,
    siweEvmSignerProfile: null,
    source: "synthetic",
    siweProfile: null,
    syntheticProfile: input.profile,
    transport: {
      mode:
        input.profile.target === "local"
          ? "synthetic-local"
          : "synthetic-staging",
      devAuthToken: input.profile.devAuthToken,
      evmAddress,
      solanaAddress,
      userId: user?.id ?? null,
      walletAddress:
        input.profile.vmPreference === "solana" && solanaAddress
          ? solanaAddress
          : input.profile.vmPreference === "evm" && evmAddress
            ? evmAddress
            : input.profile.walletAddress,
    },
    updateEmail: () => {
      warnUnsupportedSyntheticAction(
        "Synthetic dev auth does not support linked-email management.",
      );
    },
    updateSyntheticProfile: input.updateSyntheticProfile,
    user,
  };
}

type AuthUserActionName = Extract<UserActionName, `auth.${string}`>;

async function runObservedAsyncAuthAction(
  action: AuthUserActionName,
  callback: () => Promise<void>,
): Promise<void> {
  const userAction = startUserAction({
    action,
    surface: "auth",
  });
  try {
    await callback();
    userAction.succeed();
  } catch (error) {
    userAction.fail(error);
    throw error;
  }
}

function runObservedSyncAuthAction(
  action: AuthUserActionName,
  callback: () => void,
): void {
  const userAction = startUserAction({
    action,
    surface: "auth",
  });
  try {
    callback();
    userAction.succeed();
  } catch (error) {
    userAction.fail(error);
    throw error;
  }
}

function createObservedAppAuthValue(value: AppAuthValue): AppAuthValue {
  return {
    ...value,
    activateSyntheticProfile: (nextProfile) => {
      runObservedSyncAuthAction("auth.activate_synthetic_profile", () => {
        value.activateSyntheticProfile(nextProfile);
      });
    },
    clearSyntheticProfile: () => {
      runObservedSyncAuthAction("auth.clear_synthetic_profile", () => {
        value.clearSyntheticProfile();
      });
    },
    exportWallet: async () => {
      await runObservedAsyncAuthAction("auth.export_wallet", async () => {
        await value.exportWallet();
      });
    },
    linkEmail: () => {
      runObservedSyncAuthAction("auth.link_email", () => {
        value.linkEmail();
      });
    },
    login: async () => {
      await runObservedAsyncAuthAction("auth.login", async () => {
        await value.login();
      });
    },
    logout: async () => {
      await runObservedAsyncAuthAction("auth.logout", async () => {
        await value.logout();
      });
    },
    updateEmail: () => {
      runObservedSyncAuthAction("auth.update_email", () => {
        value.updateEmail();
      });
    },
    updateSiweProfile: value.updateSiweProfile
      ? (patch) => value.updateSiweProfile?.(patch)
      : undefined,
    updateSyntheticProfile: (patch) => {
      runObservedSyncAuthAction("auth.update_synthetic_profile", () => {
        value.updateSyntheticProfile(patch);
      });
    },
  };
}

export function AppAuthValueProvider(props: {
  children: ReactNode;
  value: AppAuthValue;
}) {
  const transport = props.value.transport;
  const observedValue = useMemo(
    () => createObservedAppAuthValue(props.value),
    [props.value],
  );

  useLayoutEffect(() => {
    setAppAuthTransportSnapshot(transport);
    return () => {
      setAppAuthTransportSnapshot(GUEST_TRANSPORT_SNAPSHOT);
    };
  }, [
    transport.devAuthToken,
    transport.evmAddress,
    transport.mode,
    transport.siweAccessToken,
    transport.solanaAddress,
    transport.userId,
    transport.walletAddress,
  ]);

  return (
    <AppAuthContext.Provider value={observedValue}>
      {props.children}
    </AppAuthContext.Provider>
  );
}

export function useAppAuth(): AppAuthValue {
  const contextValue = use(AppAuthContext);
  if (!contextValue) {
    throw new Error(APP_AUTH_MISSING_PROVIDER_MESSAGE);
  }

  return contextValue;
}

export function useOptionalAppAuth(): AppAuthValue | null {
  return use(AppAuthContext);
}

export { getAppAuthTransportAuthHeaders, getAppAuthTransportSnapshot };

export function isAppAuthRuntimeAvailable(input: {
  hasPrivyConfig: boolean;
}): boolean {
  return (
    input.hasPrivyConfig ||
    import.meta.env.DEV ||
    import.meta.env.MODE === "test" ||
    import.meta.env.VITE_ENABLE_WALLETLESS_SIWE_E2E === "1"
  );
}

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === "AbortError";
}

function isDefinitiveSiweAuthRejection(error: unknown): boolean {
  return (
    error instanceof SiweAuthRequestError &&
    error.status !== null &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 429
  );
}

function getSiweSessionRefreshRetryDelayMs(attempt: number): number {
  const clampedAttempt = Math.max(0, Math.min(attempt, 4));
  return Math.min(
    SIWE_SESSION_REFRESH_RETRY_MAX_DELAY_MS,
    SIWE_SESSION_REFRESH_RETRY_BASE_DELAY_MS * 2 ** clampedAttempt,
  );
}

function useSiweSessionRefresh(controller: SiweController): void {
  const { clearSiweProfile, profile, updateSiweProfile } = controller;

  useEffect(() => {
    if (!profile) {
      return;
    }

    const refreshToken = profile.refreshToken;
    const refreshDelayMs = getSiweAccessTokenRefreshDelayMs(profile);
    if (!refreshToken || refreshDelayMs === null) {
      return;
    }

    const abortController = new AbortController();
    let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const refreshSession = (attempt: number) => {
      void refreshSiweAuthSession({
        refreshToken,
        signal: abortController.signal,
      })
        .then((session) => {
          if (abortController.signal.aborted) {
            return;
          }

          updateSiweProfile(session);
        })
        .catch((error: unknown) => {
          if (abortController.signal.aborted || isAbortError(error)) {
            return;
          }

          if (
            isDefinitiveSiweAuthRejection(error) ||
            attempt >= SIWE_SESSION_REFRESH_RETRY_MAX_ATTEMPTS - 1
          ) {
            clearSiweProfile();
            return;
          }

          refreshTimeoutId = setTimeout(
            () => refreshSession(attempt + 1),
            getSiweSessionRefreshRetryDelayMs(attempt),
          );
        });
    };

    if (refreshDelayMs > 0) {
      refreshTimeoutId = setTimeout(() => refreshSession(0), refreshDelayMs);
    } else {
      refreshSession(0);
    }

    return () => {
      if (refreshTimeoutId !== null) {
        clearTimeout(refreshTimeoutId);
      }
      abortController.abort();
    };
  }, [clearSiweProfile, profile, updateSiweProfile]);
}

export function GuestAppAuthProvider(props: { children: ReactNode }) {
  const controller = useSyntheticProfileController();
  const guestValue = useMemo(
    () => createGuestAuthValue(controller),
    [controller],
  );

  return (
    <AppAuthValueProvider value={guestValue}>
      {props.children}
    </AppAuthValueProvider>
  );
}

export function SiweAuthProvider(props: { children: ReactNode }) {
  const syntheticController = useSyntheticProfileController();
  const siweController = useSiweProfileController();
  useSiweSessionRefresh(siweController);
  const value = useMemo(
    () => createSiweAuthValue(siweController, syntheticController),
    [siweController, syntheticController],
  );

  return (
    <AppAuthValueProvider value={value}>{props.children}</AppAuthValueProvider>
  );
}

export function SyntheticDevAuthProvider(props: { children: ReactNode }) {
  const controller = useSyntheticProfileController();
  const value = useMemo(
    () => createSyntheticAuthValue(controller),
    [controller],
  );

  return (
    <AppAuthValueProvider value={value}>{props.children}</AppAuthValueProvider>
  );
}

export function getAppAuthWalletVm(
  address: string | null | undefined,
): VmId | null {
  if (!address) {
    return null;
  }

  return normalizeVmId(undefined, address);
}
